/**
 * Phase 4b PR 3 — Supabase client init for the React app.
 *
 * Matches the vanilla pattern at src/renderer/js/auth.js: fetch
 * configuration from the backend `/api/config` endpoint at runtime,
 * then create the Supabase client. Cross-surface parity at the auth
 * layer is meaningful — vanilla and React both go through the same
 * config endpoint, so a single Vercel env-var change applies to both.
 *
 * Why runtime fetch over Vite VITE_* env vars:
 *   - Same React bundle works across preview / prod / local without
 *     rebuild (Vercel CI builds once on push to main).
 *   - No env-var renaming needed — backend already uses
 *     NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY which
 *     wouldn't be exposed to the browser bundle by Vite without
 *     additional config.
 *   - Matches vanilla, which retires in Phase 7 — no daughter-flow drift.
 *
 * The cost: one extra request on first paint. Mitigated by: AuthProvider
 * caches the client after first init, subsequent useUser/useAuthFetch
 * calls are synchronous against the cached client.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { apiUrl } from './apiBase';

interface AppConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  /** AD-2.1 — curated server feature flags (UI-rollout flags only). */
  flags?: Record<string, boolean>;
}

/**
 * Module-level cache. Set once after the first /api/config response;
 * subsequent calls return the same instance.
 */
let cachedClient: SupabaseClient | null = null;
let inflightInit: Promise<SupabaseClient> | null = null;
/**
 * AD-2.1 — curated server flags from /api/config, captured by the same one
 * boot-time fetch that builds the Supabase client. Read synchronously via
 * `getServerFlags()` once `getSupabaseClient()` has resolved.
 */
let cachedServerFlags: Record<string, boolean> = {};

/**
 * B149 — HAS THE BOOT READ FINISHED ASKING?
 *
 * `/api/config` answers 503 (`flags_unavailable`, or `flags_empty`) while the
 * instance serving it has never completed a `feature_flags` read: it carries
 * the three public env fields and NO flags map, on purpose, because a map of
 * `false` from an instance that has never heard from the table is a guess and
 * the client cannot tell a guess from an answer. So the flags stay UNSETTLED
 * while this module keeps asking, and `useServerFlagReady` reports
 * `ready: false` for the length of that wait — a caller that redirects on OFF
 * holds instead of acting on a value nobody has said yet.
 *
 * It becomes true when a read carries the flags, or when the bounded retry
 * gives up (on the cached values if this device has them, else on the empty
 * map every caller already treats as OFF). A caller that has already decided
 * must not be re-held, so it goes back to false in exactly ONE case
 * (`retryConfigRead`, fix-3's F4): the re-ask of a cycle that settled on the
 * EMPTY map, where nothing was ever said and so nothing could have been
 * decided on it. Cached values are real values and stay settled.
 */
let serverFlagsSettled = false;

/**
 * B149 fix-4 (round-54 critic F2) — HAS A FLAGS MAP EVER ACTUALLY ARRIVED?
 *
 * `serverFlagsSettled` is true once the WAIT ends, whether or not anything
 * was ever said — a give-up on the empty map settles it exactly like a real
 * 200 does. `flagsAuthoritative` is narrower: true only once a 200 body
 * carrying a flags map has actually been applied (the branch below that does
 * `setServerFlags(read.bootable.flags, true)`), and it is monotonic — never
 * reset to false by a later failure, because an answer once received stays
 * received. A caller that decides ONCE — Step7Welcome's CTA, the first
 * look's entry effect — reads this before treating `ready` as an answer: a
 * settle that never received a flags map is a give-up, and a give-up is not
 * an answer.
 */
let flagsAuthoritative = false;

/** Bumped by the test reset so a retry cycle left running by one test can
 *  never write into the next one. */
let cycleToken = 0;

/**
 * B149 fix-3 (round-51 critic F4) — ONE BOUNDED CYCLE PER PAGE LOAD.
 *
 * `getSupabaseClient()` is called on every `useServerFlagReady` mount and from
 * MapKit's `authorizationCallback` each time a token nears expiry. With no
 * client and no cycle in flight, every one of those calls used to start a
 * WHOLE new four-read cycle, so during an outage the read count was unbounded
 * over the life of the page — and a second cycle asked while the flags were
 * already reported settled on the empty map, which is a confident all-false
 * from a failed read.
 *
 * Once a cycle has given up, this mark stands: a later `getSupabaseClient()`
 * returns the failure that cycle ended on, at once, without asking again.
 * `retryConfigRead()` is the one door through it, and `AuthProvider`'s single
 * re-ask is its one caller.
 */
let cycleExhausted = false;
/** The failure the exhausted cycle ended on — what a later call rejects with. */
let cycleError: Error | null = null;

/** Notified whenever the flags or the client change (a retry landing, the
 *  bounded wait giving up). `useServerFlagReady` re-reads on each. */
const configListeners = new Set<() => void>();

function notifyConfigChanged(): void {
  for (const listener of [...configListeners]) {
    try {
      listener();
    } catch {
      /* a listener must never break the boot */
    }
  }
}

/**
 * Subscribe to config changes — the flags landing after a retry, or the wait
 * giving up. Returns the unsubscribe.
 */
export function subscribeServerFlags(listener: () => void): () => void {
  configListeners.add(listener);
  return () => {
    configListeners.delete(listener);
  };
}

/** B149 — true once the flag values are KNOWN (or known to be unknowable). */
export function areServerFlagsSettled(): boolean {
  return serverFlagsSettled;
}

/**
 * B149 fix-4 — true once a flags map has actually been RECEIVED (a 200
 * applied), never merely given up on. See the module-level doc above.
 */
export function isServerFlagsAuthoritative(): boolean {
  return flagsAuthoritative;
}

/**
 * Curated server feature flags exposed by `/api/config`. Empty until the
 * boot-time config fetch resolves — gate UI on `getSupabaseClient()` (or the
 * `useServerFlag` hook) so the read happens after the flags are populated.
 */
export function getServerFlags(): Record<string, boolean> {
  return cachedServerFlags;
}

/**
 * P5 offline boot — the runtime config, persisted after every successful
 * fetch so a COLD START WITHOUT NETWORK can still construct the client and
 * read the locally persisted session (founder device test 2026-07-09: a
 * killed app relaunched in airplane mode hit the sign-in wall, which hid
 * the offline run queue). Cached values are public by design (project URL +
 * anon key — fully revoked server-side — + curated UI flags). Keyed by the
 * API base so a shell rebuilt against the other environment never reuses
 * this one's config.
 */
const CONFIG_CACHE_KEY = 'tayro.appConfig.v1';

/**
 * ⚖️ B149 fix-2 (harvest-51 security, A1) — A CACHED URL HAS TO BE A URL.
 *
 * `createClient` throws SYNCHRONOUSLY on a `supabaseUrl` that is not an
 * http(s) URL (`@supabase/supabase-js`'s `validateSupabaseUrl`), and a
 * corrupted `tayro.appConfig.v1` entry is one of the two ways into that throw.
 * Truthiness was the whole check here. This is the library's own shape, so an
 * entry that could only ever throw is treated as no cache at all rather than
 * carried to a `createClient` that will reject it.
 *
 * B149 fix-4 (F3) — AND THE WIRE BODY IS THE OTHER WAY IN. `readConfigOnce`
 * applies this same check to both a 200's `supabaseUrl` and a failure body's,
 * so the same "no client can be built from this" outcome, not a synchronous
 * throw, covers a malformed URL regardless of which side sent it. A 503 body
 * with a scheme-less URL and a valid cache now boots from the cache rather
 * than rejecting outright.
 *
 * What this check is NOT (harvest security 2026-09-16, F3): a host pin. It
 * accepts any parseable http(s) URL, so a poisoned cache entry or a body from
 * a compromised origin still builds a client against that host and the OAuth
 * start would hop there. That precondition is already total compromise of our
 * origin or the device, so the residual is low; the earlier claim that this
 * closed the OAuth-start redirect was wrong and is struck.
 */
function isUsableSupabaseUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    return Boolean(new URL(trimmed));
  } catch {
    return false;
  }
}

function readCachedConfig(): AppConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { base?: string; config?: Partial<AppConfig> };
    if (parsed.base !== apiUrl('/api/config')) return null;
    const c = parsed.config;
    return c && isUsableSupabaseUrl(c.supabaseUrl) && c.supabaseAnonKey
      ? (c as AppConfig)
      : null;
  } catch {
    return null;
  }
}

/**
 * W1-LAND-FIX1 (stage 1 critic F1) — what this device's last good config said
 * about ONE flag. A read-only hint over the persisted copy, for a surface that
 * may hold its first paint for a flag only when that flag was last seen ON.
 * It is never an answer: the live value is `useServerFlagReady`. False when
 * there is no usable cache, the cache is for another API base, or the flag is
 * absent or not exactly `true`.
 */
export function isServerFlagCachedOn(name: string): boolean {
  const flags = readCachedConfig()?.flags;
  return Boolean(flags && typeof flags === 'object' && flags[name] === true);
}

/**
 * W1-TODAY-COLDLOAD — what this device's last good config said about ONE flag,
 * ON or OFF. `isServerFlagCachedOn` above can only say "last seen ON"; it cannot
 * tell a device whose cache says OFF from a device with no cache at all, and a
 * surface that must decide its first paint (Today: which of two pages to build)
 * needs exactly that difference. Same read-only hint over the persisted copy,
 * never an answer (the live value is `useServerFlagReady`):
 *   - `true` / `false`: there is a usable cached flags map. A flag the map does
 *     not carry reads `false`, as an absent flag row does everywhere else.
 *   - `undefined`: there is no usable cache (none, for another API base, not
 *     JSON, a url or key no client can be built from, or no flags map in it).
 * Additive: `isServerFlagCachedOn` and every other export are unchanged.
 */
export function getCachedServerFlag(name: string): boolean | undefined {
  const flags = readCachedConfig()?.flags;
  if (!flags || typeof flags !== 'object') return undefined;
  return flags[name] === true;
}

function writeCachedConfig(config: AppConfig): void {
  try {
    localStorage.setItem(
      CONFIG_CACHE_KEY,
      JSON.stringify({ base: apiUrl('/api/config'), config }),
    );
  } catch {
    /* best-effort — offline boot just won't be available */
  }
}

/**
 * B149 — how many times the boot reads `/api/config` before it stops asking.
 * Four, spaced by the server's own `retryAfterSeconds` (2 s), is about eight
 * seconds: long enough to cover a cold instance's first flags read, short
 * enough that a flag-gated surface is never held on an unbounded wait.
 */
const CONFIG_READ_ATTEMPTS = 4;

/**
 * ⚖️ B149 fix-3 (round-51 critic F2) — BOUNDED IN WALL CLOCK, NOT ONLY IN
 * READS. A bare `fetch` has no timeout, so four reads of a hanging endpoint
 * were four hangs: `/api/config` was measured hanging about 17 s on the dev
 * preview (round-45 D7 / B154), which made the "about eight seconds" above
 * about 74 s of `ready: false` — every caller that holds on `!ready` rendering
 * nothing throughout.
 *
 * Eight seconds is the server's OWN outbound deadline
 * (`SUPABASE_FETCH_DEADLINE_MS`, api/index.js), so a read still running at 8 s
 * is waiting on an instance that has already given up on its own database
 * call: there is nothing left to arrive.
 *
 * THE MAXIMUM, stated rather than estimated: 4 reads × 8 s + 3 waits × the
 * body's `retryAfterSeconds` = about 38 s with the 2 s this deployment sends,
 * and at most 62 s if a body ever asked for the clamped ceiling of 10 s.
 * `AuthProvider`'s single re-ask can spend that once more, and no other caller
 * starts a cycle at all (F4 above).
 */
const CONFIG_READ_TIMEOUT_MS = 8000;

/**
 * Used when the body does not say. The 503 carries `retryAfterSeconds` in its
 * BODY as well as in `Retry-After`, and the body is the field read here: the
 * header is invisible to a cross-origin fetch (the API sends no
 * `Access-Control-Expose-Headers`), which is every request the Capacitor shell
 * makes.
 */
const DEFAULT_RETRY_AFTER_SECONDS = 2;

/**
 * ⚖️ B149 fix-2 (harvest-51 security, the clamp) — BOUNDED IN WALL CLOCK, NOT
 * ONLY IN ATTEMPTS. `retryAfterSeconds` is this deployment's own advice rather
 * than an attacker's, but the word "bounded" has to be true in both directions:
 * a body saying 3600 would hold `ready: false` for an hour (every caller that
 * waits on `!ready` renders its loading frame for that hour), and a body saying
 * 1e9 overflows `setTimeout`'s 32-bit delay and collapses the spacing into a
 * burst of four immediate reads. Ten seconds is the longest single wait.
 */
const MAX_RETRY_AFTER_SECONDS = 10;

interface ConfigRead {
  /** Env fields good enough to construct the client, or null. */
  bootable: AppConfig | null;
  /** True only when the body carried the flags map — i.e. a 200. */
  flagsAuthoritative: boolean;
  /**
   * B149 fix-3 (F3) — TRUE WHEN NO STATUS EVER ARRIVED: offline, DNS, TLS, or
   * the 8 s abort firing BEFORE a response's headers land. A status (503,
   * 500) is this deployment ANSWERING, which is why the wait exists; a
   * transport failure carries no news at all, and on a device holding a
   * cached config there is nothing to wait for.
   *
   * B149 fix-4 (F6) — the abort by itself is not "transport": `sawStatus`
   * below is what tells the two apart, because an abort firing AFTER headers
   * already arrived (the body still being read) is a response this deployment
   * sent, not a silence.
   */
  transport: boolean;
  /** What the server asked us to wait (clamped to `MAX_RETRY_AFTER_SECONDS`),
   *  or the default. */
  retryAfterSeconds: number;
  error: Error | null;
}

/** One read of `/api/config`, bounded at `CONFIG_READ_TIMEOUT_MS`. Never
 *  throws: every outcome is a `ConfigRead`. */
async function readConfigOnce(): Promise<ConfigRead> {
  // An AbortController rather than `AbortSignal.timeout`: the shell's WKWebView
  // is older than that API on some of the iOS versions Tayro still supports,
  // and this one is a plain `setTimeout` every environment already has.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG_READ_TIMEOUT_MS);
  /** Did a status ever arrive? Everything below the headers is not transport. */
  let sawStatus = false;
  try {
    const res = await fetch(apiUrl('/api/config'), {
      method: 'GET',
      signal: controller.signal,
    });
    sawStatus = true;
    if (res.ok) {
      const fresh = (await res.json()) as Partial<AppConfig>;
      // B149 fix-4 (F3) — the wire URL is validated the same way the cache is:
      // a 200 whose `supabaseUrl` cannot become a client is not a usable read.
      if (!isUsableSupabaseUrl(fresh.supabaseUrl) || !fresh.supabaseAnonKey) {
        throw new Error(
          'Malformed /api/config response: missing or unusable supabaseUrl, or missing supabaseAnonKey',
        );
      }
      return {
        bootable: fresh as AppConfig,
        flagsAuthoritative: true,
        transport: false,
        retryAfterSeconds: DEFAULT_RETRY_AFTER_SECONDS,
        error: null,
      };
    }
    // B149 — THE BODY IS READ ON A FAILURE TOO. The 503 the server sends while
    // its flags have never loaded carries the same three public env fields
    // every 200 carries; reading them is what lets auth and the athlete's own
    // reads proceed while the flags are still unknown. A 500 (B154's breaker
    // shape) usually carries nothing, and that is fine: `bootable` is null and
    // the cached config takes over.
    let body: (Partial<AppConfig> & { retryAfterSeconds?: unknown }) | null = null;
    try {
      body = (await res.json()) as Partial<AppConfig> & { retryAfterSeconds?: unknown };
    } catch {
      /* a 500 need not be JSON */
    }
    const asked = Number(body?.retryAfterSeconds);
    return {
      // B149 fix-4 (F3) — the same validation as the 200 branch above: a
      // failure body whose `supabaseUrl` cannot become a client boots from
      // the cache instead (below, in `runConfigCycle`), rather than handing
      // `createClient` a URL it will throw on.
      bootable:
        body && isUsableSupabaseUrl(body.supabaseUrl) && body.supabaseAnonKey
          ? { supabaseUrl: body.supabaseUrl, supabaseAnonKey: body.supabaseAnonKey }
          : null,
      flagsAuthoritative: false,
      transport: false,
      retryAfterSeconds:
        Number.isFinite(asked) && asked > 0
          ? Math.min(asked, MAX_RETRY_AFTER_SECONDS)
          : DEFAULT_RETRY_AFTER_SECONDS,
      error: new Error(`Failed to load Supabase config: ${res.status}`),
    };
  } catch (err) {
    return {
      bootable: null,
      flagsAuthoritative: false,
      // A malformed 200 (the throw above, or a body that is not JSON) got a
      // status: this deployment answered, it just answered nonsense, so the
      // retries are still worth spending.
      transport: !sawStatus,
      retryAfterSeconds: DEFAULT_RETRY_AFTER_SECONDS,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * lane-signin-followup (R14) — a later switch of the Supabase host to
 * `auth.tayro.run` (a stated step in the go-live plan) is not itself a
 * project migration; the project ref stays `zkwbrjvfqwmfflxjkbfr`. Mapped
 * here so `deriveStorageKey` still finds the right ref even if the anon key
 * ever ships as one of the newer non-JWT `sb_publishable_...` keys, which
 * carry no `ref` claim to read.
 */
const NATIVE_AUTH_HOST_PROJECT_REF: Record<string, string> = {
  'auth.tayro.run': 'zkwbrjvfqwmfflxjkbfr',
};

/** Best-effort read of a classic Supabase anon key's `ref` JWT claim. Never
 *  throws — a key that is not a parseable JWT (including the newer
 *  `sb_publishable_...` key format) just means this path found nothing. */
function projectRefFromAnonKeyClaim(anonKey: string): string | null {
  try {
    const payload = anonKey.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { ref?: unknown };
    return typeof claims.ref === 'string' && claims.ref ? claims.ref : null;
  } catch {
    return null;
  }
}

/**
 * lane-signin-followup (R14) — pin the saved-session storage key to the
 * project REF, not to whatever host happens to be in `supabaseUrl` at boot.
 * supabase-js's own default (`sb-${hostname.split('.')[0]}-auth-token`) reads
 * the URL's first host label: today that IS the project ref
 * (`https://zkwbrjvfqwmfflxjkbfr.supabase.co`), but a later switch to
 * `https://auth.tayro.run` would read `auth` instead, minting a brand-new
 * key and signing every already-installed client out. `undefined` means
 * "no ref found" — the library default applies, unchanged from today.
 */
function deriveStorageKey(config: AppConfig): string | undefined {
  let hostname: string;
  try {
    hostname = new URL(config.supabaseUrl).hostname;
  } catch {
    return undefined;
  }
  const ref = hostname.endsWith('.supabase.co')
    ? hostname.split('.')[0]
    : (projectRefFromAnonKeyClaim(config.supabaseAnonKey) ??
      NATIVE_AUTH_HOST_PROJECT_REF[hostname] ??
      null);
  return ref ? `sb-${ref}-auth-token` : undefined;
}

function createSupabaseClient(config: AppConfig): SupabaseClient {
  const storageKey = deriveStorageKey(config);
  cachedClient = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // B207 — PKCE over the existing tayro:// scheme (spec direction B):
      // the native callback now carries a one-time code, never the raw
      // access/refresh tokens, so an app also claiming the scheme gets a
      // code it cannot redeem (no matching code_verifier). One shared
      // client for web and native; the web redirect keeps working
      // unchanged — the SDK's own detectSessionInUrl exchanges `?code=`
      // automatically. See lib/nativeAuth.ts for the native exchange.
      flowType: 'pkce',
      // lane-signin-followup (R14) — see deriveStorageKey above. Omitted
      // entirely when no ref is found, so the library's own default applies
      // exactly as it does today.
      ...(storageKey ? { storageKey } : {}),
    },
  });
  return cachedClient;
}

/**
 * ⚖️ B149 fix-2 (harvest-51 security, A1) — A CLIENT THAT CANNOT BE BUILT IS A
 * FAILED READ, NEVER AN ESCAPED EXCEPTION.
 *
 * `createClient` throws synchronously on a malformed `supabaseUrl` (a bad
 * `NEXT_PUBLIC_SUPABASE_URL` on the deployment, a corrupt cache entry). That
 * throw used to escape `runConfigCycle` mid-loop, so the settle at the end of
 * the loop never ran and `serverFlagsSettled` stayed false for the life of the
 * page: every caller that holds on `!ready` — and after this lane that
 * includes the first look and the Step 7 CTA — would hold for ever. An
 * unbounded wait is the one thing the bounded cycle exists to prevent, so a
 * config no client can be built from is treated exactly like a read that
 * failed: the loop keeps its shape, retries, and settles.
 */
function tryCreateSupabaseClient(config: AppConfig): SupabaseClient | null {
  try {
    return createSupabaseClient(config);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[auth] /api/config carried a config no client could be built from:', err);
    return null;
  }
}

function setServerFlags(
  flags: Record<string, boolean> | undefined,
  settled: boolean,
): void {
  cachedServerFlags = flags && typeof flags === 'object' ? flags : {};
  if (settled) serverFlagsSettled = true;
}

/**
 * ⚖️ B149 fix-3 (round-51 critic F3) — AN OFFLINE COLD START HOLDS NOTHING.
 *
 * A read that failed at the TRANSPORT layer got no status: the request never
 * reached a deployment, so nothing it could have said is being waited for, and
 * the last values this deployment really did say are already on the device.
 * Burning the three 2 s waits before settling on those same values held every
 * flag-gated surface — the first look, the Step 7 CTA, "Draft my week" — for
 * about six seconds in airplane mode, for nothing.
 *
 * So the cached values settle AT ONCE and the retries keep running behind
 * them: a read that lands inside the window still updates the values (settling
 * is about the wait ending, not about the values freezing). A 503 or a 500 is
 * this deployment answering, and that wait is kept.
 *
 * Returns true when it settled something, so the caller can notify.
 */
function settleOnCachedFlags(): boolean {
  if (serverFlagsSettled) return false;
  const cached = readCachedConfig();
  // No cached FLAGS is not an answer either: an empty map from a failed read is
  // the guess B149 exists to refuse, so that case keeps the wait.
  if (!cached?.flags || typeof cached.flags !== 'object') return false;
  setServerFlags(cached.flags, true);
  return true;
}

function waitSeconds(seconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
  });
}

/**
 * B149 — THE BOOT NEVER WAITS ON FLAGS, AND THE FLAGS ARE NEVER GUESSED.
 *
 * One cycle of at most `CONFIG_READ_ATTEMPTS` reads. `onClient` fires the
 * moment a client can be built — from a 200, from the 503 body's env fields,
 * or from the last good config in localStorage — so auth, the session and the
 * athlete's own reads proceed immediately. The remaining attempts run behind
 * that, purely to learn the flags; when they are exhausted the flags settle on
 * the cached values if this device has them, else on the empty map.
 * `onNoClient` fires only if the whole cycle produced nothing to boot on.
 */
async function runConfigCycle(
  token: number,
  onClient: (client: SupabaseClient) => void,
  onNoClient: (err: Error) => void,
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= CONFIG_READ_ATTEMPTS; attempt += 1) {
    const read = await readConfigOnce();
    if (token !== cycleToken) return;
    if (read.error) lastError = read.error;

    // F3 — a transport failure on a device that holds this deployment's last
    // word: settle on it now, keep asking behind it.
    if (read.transport && settleOnCachedFlags()) notifyConfigChanged();

    if (read.bootable && read.flagsAuthoritative) {
      const booted = cachedClient;
      const client = booted ?? tryCreateSupabaseClient(read.bootable);
      if (client) {
        writeCachedConfig(read.bootable);
        setServerFlags(read.bootable.flags, true);
        // B149 fix-4 (F2) — the ONE place this becomes true: a 200's flags
        // map, actually applied. Never set anywhere else, and never reset.
        flagsAuthoritative = true;
        if (!booted) onClient(client);
        notifyConfigChanged();
        return;
      }
      // A 200 whose env fields no client can be built from is not an answer
      // this device can use: it is neither cached nor settled from, and the
      // loop carries on to the wait and the settle below.
      lastError = new Error('Failed to build the Supabase client from /api/config');
    }

    if (!cachedClient) {
      // The 503's own env fields first (this deployment's answer about
      // itself), then the last successful config — P5 offline boot, so the
      // persisted session and the offline run queue behind it stay reachable.
      const cached = readCachedConfig();
      const bootConfig = read.bootable ?? cached;
      if (bootConfig) {
        if (!read.bootable) {
          // eslint-disable-next-line no-console
          console.warn(
            '[auth] /api/config unreachable — booting from cached config:',
            lastError,
          );
        }
        // The cached flags are the last values this deployment really said, so
        // they are what `getServerFlags()` reports while the retries run. They
        // are deliberately NOT settled: `ready` stays false until asking stops.
        if (cached) setServerFlags(cached.flags, false);
        const client = tryCreateSupabaseClient(bootConfig);
        if (client) {
          onClient(client);
          notifyConfigChanged();
        }
      }
    }

    if (attempt < CONFIG_READ_ATTEMPTS) {
      await waitSeconds(read.retryAfterSeconds);
      if (token !== cycleToken) return;
    }
  }

  // Out of attempts. Settle on what this device knows rather than hold a
  // surface open forever — the cached flags if there are any, else the empty
  // map, which is the failed state every caller already handles.
  setServerFlags(cachedServerFlags, true);
  notifyConfigChanged();
  if (!cachedClient) {
    // F4 — this cycle asked four times and got nothing to boot on. Every later
    // caller gets this answer instead of a fresh cycle, until `retryConfigRead`
    // opens the one door.
    cycleError = lastError ?? new Error('Failed to load Supabase config');
    cycleExhausted = true;
    onNoClient(cycleError);
  }
}

/**
 * Fetch /api/config and create (or return cached) SupabaseClient.
 * Idempotent — safe to call multiple times. Concurrent calls share
 * the same in-flight promise rather than racing.
 *
 * Resolves as soon as ANY config this device can reach yields a client (a 200,
 * a 503's public env fields, or the last successful config); rejects only when
 * the whole bounded cycle found nothing. Callers (AuthProvider) catch and
 * surface as auth-init error.
 */
export async function getSupabaseClient(): Promise<SupabaseClient> {
  if (cachedClient) return cachedClient;
  if (inflightInit) return inflightInit;
  // F4 — a cycle already asked, four times, and gave up. This is a static
  // mark, not a check for whether anything has changed: it holds until
  // fix-4's recovery clears it (`clearExhaustedMark` below, on a
  // `visibilitychange` to visible or an `online` event) or a test resets the
  // module — until then every caller answers with what that cycle found
  // rather than spending another one.
  if (cycleExhausted) throw cycleError ?? new Error('Failed to load Supabase config');

  const token = cycleToken;
  inflightInit = new Promise<SupabaseClient>((resolve, reject) => {
    void runConfigCycle(token, resolve, reject)
      .then(() => {
        // Harvest 2026-10-20 L1 — as in `retryConfigRead` below: a client that
        // stood by the time this cycle ended (the landing's late ask built it
        // while this cycle was out) never triggers `onClient` or `onNoClient`,
        // so without this the promise would neither resolve nor reject and
        // `inflightInit` would stay set for the life of the page.
        if (cachedClient) resolve(cachedClient);
      })
      .catch((err: unknown) => {
        // A throw out of the cycle must not leave the flags unsettled: every
        // caller that holds on `ready` would hold for the life of the page, which
        // is the unbounded wait the bounded cycle exists to prevent. fix-2 closed
        // the one known throw (`createClient`); this is the guarantee behind it.
        if (token === cycleToken && !serverFlagsSettled) {
          setServerFlags(cachedServerFlags, true);
          notifyConfigChanged();
        }
        reject(err);
      });
  });

  try {
    return await inflightInit;
  } finally {
    // Clear the in-flight pointer once the cycle settles, success or failure
    // alike. A success is harmless to clear too — every later call already
    // short-circuits on `cachedClient` above before it would ever be
    // consulted — and B149 fix-4's `retryConfigRead` needs it cleared to tell
    // "a cycle is running right now" apart from "the last cycle's promise,
    // long since resolved".
    inflightInit = null;
  }
}

/**
 * B149 fix-4 — bounds the DECISION-TIME re-ask (Step7Welcome's CTA, the first
 * look's entry effect) to at most ONE extra cycle per page load, shared with
 * `AuthProvider`'s own re-ask: both go through `retryConfigRead` below, and
 * this is what keeps the total read count over a page load bounded now that
 * a second kind of caller exists. Never reset except by the test helper.
 */
let reaskSpent = false;

/**
 * B149 fix-3 (F4) — THE ONE DOOR THROUGH THE EXHAUSTED MARK.
 *
 * `AuthProvider`'s single re-ask was the only caller through fix-3; fix-4
 * adds the two decision-time callers (Step7Welcome, the first look), so
 * "the one place that knows a whole cycle has been spent" is no longer a
 * single call site — `reaskSpent` above is what keeps it to ONE cycle
 * regardless of how many places ask. Every other caller still goes through
 * `getSupabaseClient()` and gets the failed state without a new cycle.
 *
 * If the flags settled on the EMPTY map — nothing was ever said, so nothing was
 * decided — `ready` goes back to unknown for the length of this cycle rather
 * than reporting an empty map as an answer while the module is still asking
 * (round-51 F4's second-order point). Cached values are real values and stay
 * settled.
 *
 * B149 fix-4 (F2) — AND A CLIENT ALREADY STANDING THERE IS NOT A REASON TO
 * SKIP THE ASK. `getSupabaseClient()` returns `cachedClient` at once, which is
 * right for every ordinary caller — but PROBE F is exactly a client built from
 * a give-up (the 503 body's own env fields, or the cache) whose FLAGS never
 * arrived, so this function runs `runConfigCycle` directly rather than
 * delegating to `getSupabaseClient()`, and answers with the client either way
 * once the cycle ends: an authoritative read updates the flags in place, a
 * failed one leaves them exactly as they were and the caller decides on the
 * non-authoritative value.
 */
export async function retryConfigRead(): Promise<SupabaseClient> {
  if (inflightInit) return inflightInit;
  if (cachedClient && flagsAuthoritative) return cachedClient;
  if (reaskSpent) return cachedClient ?? getSupabaseClient();
  reaskSpent = true;
  cycleExhausted = false;
  cycleError = null;
  if (serverFlagsSettled && Object.keys(cachedServerFlags).length === 0) {
    serverFlagsSettled = false;
    notifyConfigChanged();
  }

  const token = cycleToken;
  inflightInit = new Promise<SupabaseClient>((resolve, reject) => {
    void runConfigCycle(token, resolve, reject)
      .then(() => {
        // A client that already existed going in never triggers `onClient`
        // (that callback only fires for a NEW client, above) or `onNoClient`
        // (guarded on `!cachedClient`, above) — this cycle ran for the flags
        // alone, and the client it had all along is still the answer.
        if (cachedClient) resolve(cachedClient);
      })
      .catch((err: unknown) => {
        if (token === cycleToken && !serverFlagsSettled) {
          setServerFlags(cachedServerFlags, true);
          notifyConfigChanged();
        }
        if (cachedClient) resolve(cachedClient);
        else reject(err);
      });
  });

  try {
    return await inflightInit;
  } finally {
    // Always clear — see `getSupabaseClient`'s matching comment above.
    inflightInit = null;
  }
}

/**
 * B398 (founder ruling 2026-10-20, item 6) — THE LANDING'S OWN LATE ASK.
 *
 * The public landing shows "Sign in with Apple" only on `apple_login_v1`, and
 * that flag rides the same `/api/config` read as everything else. A page that
 * loaded during an outage spends its boot cycle and `AuthProvider`'s one
 * re-ask, gives up, and never asks again, so an Apple-only visitor saw only
 * "Continue with Google" for the life of the page even after the config came
 * back.
 *
 * This is the narrow way back, and it is deliberately NOT `retryConfigRead`:
 * that door is the one re-ask per page load that `AuthProvider`, Step7Welcome
 * and the first look share, and spending it here could take it from them. This
 * one has a budget of its own:
 *   · at most `LANDING_REASK_READ_CAP` reads over the life of a page, one read
 *     each (a single `readConfigOnce`, bounded at 8 s), never a cycle;
 *   · only once the flags have SETTLED without ever being received (a cycle
 *     still asking is its own answer), never while a cycle is in flight, never
 *     once a flags map has arrived;
 *   · only when the landing asks, on a visitor-driven signal (a surface
 *     mounting or opening, the tab coming back, the network returning): there
 *     is no timer, no loop, no polling.
 *
 * It changes nothing unless it hears a real answer. A failed, hung or malformed
 * read leaves every flag, mark and counter of the module exactly as it was
 * (a sign-in flag never fails open), and a spent budget stays spent. A 200
 * with a flags map is applied exactly as the boot cycle applies it, so
 * `useServerFlagReady` subscribers re-render with the real value.
 *
 * Resolves when the ask is over, never rejects. Concurrent callers share the
 * one read in flight.
 */
export const LANDING_REASK_READ_CAP = 2;
let landingReaskReads = 0;
let landingReaskInFlight: Promise<void> | null = null;

export function reaskFlagsForLanding(): Promise<void> {
  if (landingReaskInFlight) return landingReaskInFlight;
  if (flagsAuthoritative || !serverFlagsSettled || inflightInit) return Promise.resolve();
  if (landingReaskReads >= LANDING_REASK_READ_CAP) return Promise.resolve();
  landingReaskReads += 1;

  const token = cycleToken;
  const ask: Promise<void> = (async () => {
    const read = await readConfigOnce();
    // A reset, or an answer that landed while this read was out, wins.
    if (token !== cycleToken || flagsAuthoritative) return;
    if (!read.bootable || !read.flagsAuthoritative) return;
    const client = cachedClient ?? tryCreateSupabaseClient(read.bootable);
    if (!client) return;
    writeCachedConfig(read.bootable);
    setServerFlags(read.bootable.flags, true);
    flagsAuthoritative = true;
    notifyConfigChanged();
  })()
    .catch(() => {
      /* a late ask must never break the page it runs on */
    })
    .finally(() => {
      if (landingReaskInFlight === ask) landingReaskInFlight = null;
    });
  landingReaskInFlight = ask;
  return ask;
}

/**
 * B149 fix-4 (round-57 critic F4) — THE MARK IS NOT FOR THE PAGE'S WHOLE LIFE.
 *
 * `cycleExhausted` used to stand until a reload, and the shell never reloads:
 * a first launch on a bad network with no cache had no way back once its one
 * bounded cycle gave up, not even once the network came back. These are the
 * two signals a phone actually gives for "conditions changed" — coming back
 * to the foreground, and the network reporting itself present again.
 * Clearing the mark does not itself start a cycle; it only lets the NEXT
 * `getSupabaseClient()` caller open one.
 *
 * B167 fix-2 — exported so a caller that IS the next `getSupabaseClient()`
 * (Today's config-hold "Try again", `routes/today.tsx`) can open the same
 * door the `online`/`visibilitychange` listeners below use, rather than
 * waiting on one of those signals to fire first.
 */
export function clearExhaustedMark(): void {
  if (!cycleExhausted) return;
  cycleExhausted = false;
  cycleError = null;
}

let recoveryListenersRegistered = false;

/** Registered once per module load (guarded so a test re-import never
 *  stacks duplicate listeners on the shared jsdom `window`). */
function registerRecoveryListeners(): void {
  if (recoveryListenersRegistered) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  recoveryListenersRegistered = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') clearExhaustedMark();
  });
  window.addEventListener('online', clearExhaustedMark);
}
registerRecoveryListeners();

/**
 * Test/dev helper — clear the cached client so tests can re-init with
 * a fresh fetch mock. Production code should never call this.
 */
export function _resetSupabaseClientForTests(): void {
  cachedClient = null;
  inflightInit = null;
  cachedServerFlags = {};
  serverFlagsSettled = false;
  flagsAuthoritative = false;
  reaskSpent = false;
  landingReaskReads = 0;
  landingReaskInFlight = null;
  cycleExhausted = false;
  cycleError = null;
  // Any retry cycle still running belongs to the previous test: orphan it.
  cycleToken += 1;
}
